import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpcQuery } from "../../ipc/IpcProvider";
import type { EngineOverviewDto, PeriodRange } from "../../ipc/types";
import { useCsvExport } from "../../lib/useCsvExport";
import { useTableView } from "../../lib/useTableView";
import { TableToolbar, TablePagination } from "../../components/TableControls";
import { useDateRange } from "../../components/DateRangeContext";
import { formatRange } from "../../lib/date-range";
import { CopyDataButton } from "../../components/CopyDataButton";
import { strings } from "../../i18n/es-CL";
import "../dashboard/dashboard.css";

export interface EngineScreenProps {
  engineId: string;
  icon: IconName;
  subtitle: string;
}

export function EngineScreen({ engineId, icon, subtitle }: EngineScreenProps): JSX.Element {
  const { range, previous, compare } = useDateRange();
  const periodRange: PeriodRange = {
    fromEpoch: range.fromEpoch, toEpoch: range.toEpoch,
    prevFromEpoch: compare ? previous.fromEpoch : 0,
    prevToEpoch: compare ? previous.toEpoch : 0,
  };
  const { state, reload } = useIpcQuery<EngineOverviewDto>(
    (c) => c.getEngineOverview(engineId, periodRange),
    [engineId, range.fromEpoch, range.toEpoch, previous.fromEpoch, previous.toEpoch, compare],
  );
  const empty = { title: "", columns: [], rows: [] };
  const { exportCsv, exporting, banner } = useCsvExport(state.status === "ready" ? state.data : empty);
  const view = useTableView(state.status === "ready" ? state.data.rows : []);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const d = state.data;

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}>
              <Icon name={icon} size={22} />
            </div>
            <div>
              <h1 className="pagehead__title">{d.title}</h1>
              <p className="pagehead__sub">{subtitle}</p>
              {d.period ? (
                <div className="row" style={{ gap: 8, marginTop: 4 }}>
                  <Badge tone="brand" dot>{formatRange({ fromEpoch: d.period.fromEpoch, toEpoch: d.period.toEpoch })}</Badge>
                  {compare ? <span className="faint" style={{ fontSize: 12 }}>vs período anterior</span> : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: d.title, columns: d.columns, rows: d.rows, metrics: d.metrics, ...(d.period ? { periodLabel: formatRange(d.period) } : {}) }} />
            <Button variant="secondary" size="sm" icon="analytics" disabled={exporting || d.rows.length === 0} onClick={exportCsv}>
              {exporting ? "Exportando…" : strings.engine.export}
            </Button>
          </div>
        </div>
      </div>

      {banner}

      <div className="kpis" style={{ marginTop: 0 }}>
        {d.metrics.map((m) => (
          <StatCard
            key={m.label}
            label={m.label}
            value={m.value}
            icon={m.icon as IconName}
            accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})}
          />
        ))}
      </div>

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader
          title={strings.engine.activity}
          action={<Badge tone="brand" dot>Sincronizado</Badge>}
        />
        <div className="card__body" style={{ padding: 0 }}>
          {d.rows.length === 0 ? (
            <div className="statecard">
              <Icon name={icon} size={26} style={{ color: "var(--text-faint)" }} />
              <div className="strong">Aún no hay datos</div>
              <div className="faint" style={{ fontSize: 13 }}>Este motor todavía no tiene registros que mostrar.</div>
            </div>
          ) : (
            <>
              <TableToolbar view={view} />
              {view.rows.length === 0 ? (
                <div className="statecard"><div className="faint" style={{ fontSize: 13 }}>Ningún registro coincide con la búsqueda.</div></div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      {d.columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {view.rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="strong">{row.primary}</div>
                        </td>
                        <td className="muted">{row.secondary}</td>
                        <td>
                          <Badge tone={row.status.tone} dot>
                            {row.status.label}
                          </Badge>
                        </td>
                        <td className="tnum strong">{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <TablePagination view={view} />
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
